import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Skeleton, SkeletonCard, SkeletonLine } from '@/components/ui/skeleton';
import { SegmentControl } from '@/components/ui/segment-control';
import { api } from '@/lib/api';
import { getWebApp, haptic } from '@/lib/telegram';
import {
  Receipt,
  CheckCircle2,
  Clock,
  XCircle,
  AlertCircle,
  ExternalLink,
  Copy,
  Check,
  Zap,
  Ticket,
} from 'lucide-react';
import { toast } from 'sonner';
import { MoneySwitch } from '@/components/SectionSwitch';

export const Route = createFileRoute('/payouts')({ component: Payouts });

type Filter = 'all' | 'completed' | 'pending' | 'rejected';

function Payouts() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<Filter>('all');
  const { data: partner } = useQuery({ queryKey: ['partner'], queryFn: () => api.me() });
  const { data: items, isPending } = useQuery({
    queryKey: ['payouts'],
    queryFn: () => api.partnerPayouts(),
    refetchInterval: (q) => {
      // Если есть pending — поллим каждые 10с (вдруг owner approve'нет).
      const arr = (q.state.data as Payout[] | undefined) ?? [];
      return arr.some((p) => p.status === 'pending' || p.status === 'approved') ? 10_000 : false;
    },
  });

  useEffect(() => {
    const tg = getWebApp();
    if (!tg?.BackButton) return;
    tg.BackButton.show();
    const onBack = () => navigate({ to: '/finance' });
    tg.BackButton.onClick(onBack);
    return () => tg.BackButton.offClick(onBack);
  }, [navigate]);

  const filtered = (items || []).filter((p) => {
    if (filter === 'all') return true;
    if (filter === 'completed') return p.status === 'completed';
    if (filter === 'pending') return p.status === 'pending' || p.status === 'approved';
    if (filter === 'rejected') return p.status === 'rejected' || p.status === 'failed';
    return true;
  });

  const totalCompleted = (items || [])
    .filter((p) => p.status === 'completed')
    .reduce((sum, p) => sum + p.amount_usd_minor, 0);
  const pendingCount = (items || []).filter((p) => p.status === 'pending' || p.status === 'approved').length;

  return (
    <div className="p-4 space-y-4 pb-24">
      <MoneySwitch />
      <div className="px-1 pt-2">
        <h2 className="font-display text-2xl font-bold tracking-tight mb-1">История выводов</h2>
        <p className="text-xs text-muted-foreground">
          Все запросы на вывод. Новый вывод делается в «Кошельке».
        </p>
      </div>

      {items && items.length > 0 && (
        <div className="grid grid-cols-2 gap-[11px]">
          <div className="surface-card rounded-[20px] p-[15px]">
            <div className="eyebrow mb-1.5">Всего выведено</div>
            <div className="font-display font-bold text-xl nums">${(totalCompleted / 100).toFixed(2)}</div>
          </div>
          <div className="surface-card rounded-[20px] p-[15px]">
            <div className="eyebrow mb-1.5">В обработке</div>
            <div className="font-display font-bold text-xl nums">{pendingCount}</div>
          </div>
        </div>
      )}

      {items && items.length > 0 && (
        <SegmentControl<Filter>
          value={filter}
          onChange={setFilter}
          options={[
            { value: 'all', label: 'Все' },
            { value: 'completed', label: 'Выплачено' },
            { value: 'pending', label: 'В обработке' },
            { value: 'rejected', label: 'Отклонено' },
          ]}
        />
      )}

      <div className="space-y-3">
        {isPending && !items ? (
          <PayoutsSkeleton />
        ) : !items || items.length === 0 ? (
          <div className="surface-card rounded-[22px] py-11 px-6 text-center">
            <div className="w-[54px] h-[54px] rounded-[17px] bg-white/[0.05] flex items-center justify-center mx-auto mb-3.5 text-[#6b6d75]">
              <Receipt className="w-[26px] h-[26px]" strokeWidth={1.5} />
            </div>
            <div className="font-bold text-[15px] text-muted-foreground">Пока ничего</div>
            <div className="text-[12.5px] text-dim mt-1.5">
              {partner?.isDelegate
                ? 'Выводы запрашивает владелец кабинета.'
                : 'Запросить вывод можно в разделе «Финансы».'}
            </div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="surface-card rounded-[22px] p-6 text-center text-sm text-muted-foreground">
            Нет выводов в этом статусе.
          </div>
        ) : (
          filtered.map((p) => <PayoutCard key={p.id} p={p} />)
        )}
      </div>
    </div>
  );
}

type Payout = Awaited<ReturnType<typeof api.partnerPayouts>>[number];

/** Лейблы способов вывода в истории. Раньше здесь был хардкод на три метода, из-за
 *  которого `sbp`/`trc20`/`bep20`/`ton` показывались партнёру сырыми строками.
 *  Держать в синхроне с `PAYOUT_METHOD_FEES` (`application/payouts/service.py`). */
const METHOD_LABELS: Record<string, string> = {
  cryptobot: 'CryptoBot (USDT)',
  ton: 'TON (on-chain)',
  sbp: 'СБП (по номеру телефона)',
  trc20: 'USDT TRC-20',
  bep20: 'USDT BEP-20 (BSC)',
  bybit: 'Bybit (перевод по UID)',
  xrocket: 'xRocket (USDT)',
  ton_usdt: 'USDT в сети TON',
  usdt_sol: 'USDT в сети Solana',
  usdc_sol: 'USDC в сети Solana',
  manual: 'Ручной перевод',
  // Legacy-значения из старых заявок.
  crypto: 'CryptoBot USDT',
  card: 'Банковская карта',
};

function PayoutCard({ p }: { p: Payout }) {
  const [copied, setCopied] = useState(false);
  const [checkCopied, setCheckCopied] = useState(false);
  const usd = (p.amount_usd_minor / 100).toFixed(2);
  const methodLabel = METHOD_LABELS[p.method] ?? p.method;

  const isCompleted = p.status === 'completed';
  const isRejected = p.status === 'rejected' || p.status === 'failed';
  // Перевод в пути: деньги ушли или уходят, «выплачено» поставит подтверждение сети.
  const inFlight = !isCompleted && (p.transfer_state === 'sending' || p.transfer_state === 'sent');

  const statusCfg = isCompleted
    ? { cls: 'bg-[rgba(56,224,165,0.14)] text-[#38e0a5]', label: 'Выплачено', Icon: CheckCircle2, descCls: 'bg-[rgba(56,224,165,0.06)] text-[#38e0a5]', desc: 'Деньги отправлены' }
    : isRejected
      ? { cls: 'bg-[rgba(248,113,113,0.14)] text-[#f87171]', label: 'Отклонено', Icon: XCircle, descCls: 'bg-[rgba(248,113,113,0.06)] text-[#f87171]', desc: 'Заявка не прошла. Средства вернулись на баланс.' }
      : inFlight
        ? { cls: 'bg-white/[0.07] text-muted-foreground', label: 'Отправляется', Icon: Clock, descCls: 'bg-white/[0.07] text-muted-foreground', desc: p.method === 'ton' ? 'Перевод в сети TON, ждём подтверждения. Обычно это пара минут.' : 'Чек выписывается, ссылка появится здесь.' }
        : { cls: 'bg-[rgba(240,176,78,0.15)] text-[#f0b04e]', label: 'В обработке', Icon: Clock, descCls: 'bg-[rgba(240,176,78,0.06)] text-[#f0b04e]', desc: p.status === 'approved' ? 'Одобрено, ждёт отправки' : 'Ждёт рассмотрения админом (≤24ч)' };

  const checkUrl = p.external_ref && p.external_ref.startsWith('http') ? p.external_ref : null;

  const openCheck = () => {
    if (!checkUrl) return;
    const tg = (window as { Telegram?: { WebApp?: { openTelegramLink?: (u: string) => void; openLink?: (u: string) => void } } }).Telegram?.WebApp;
    if (tg?.openTelegramLink && checkUrl.includes('t.me/')) tg.openTelegramLink(checkUrl);
    else if (tg?.openLink) tg.openLink(checkUrl);
    else window.open(checkUrl, '_blank');
  };

  const copyDestination = async () => {
    try {
      await navigator.clipboard.writeText(p.destination);
      setCopied(true);
      haptic('success');
      toast.success('Скопировано');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Не удалось');
    }
  };

  // Ссылку чека даём копировать всегда — если CryptoBot недоступен (забанен в регионе),
  // партнёр откроет ссылку в браузере/на другом устройстве. Кнопка «Получить» этого не
  // покрывает: openTelegramLink ведёт в тот же недоступный CryptoBot.
  const copyCheckUrl = async () => {
    if (!checkUrl) return;
    try {
      await navigator.clipboard.writeText(checkUrl);
      setCheckCopied(true);
      haptic('success');
      toast.success('Ссылка чека скопирована');
      setTimeout(() => setCheckCopied(false), 2000);
    } catch {
      toast.error('Не удалось скопировать');
    }
  };

  const dateFull = new Date(p.created_at).toLocaleString('ru', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });

  return (
    <div className="surface-card rounded-[20px] p-4 space-y-3">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="flex items-baseline gap-2">
            <div className="font-display text-2xl font-bold nums">${usd}</div>
            {p.auto_executed && (
              <span className="text-[10px] font-bold uppercase text-muted-foreground flex items-center gap-0.5">
                <Zap className="w-3 h-3" />
                авто
              </span>
            )}
          </div>
          <div className="text-[11px] text-dim">{dateFull}</div>
        </div>
        <span className={`text-[10px] font-bold px-2 py-1 rounded-full flex items-center gap-1 shrink-0 ${statusCfg.cls}`}>
          <statusCfg.Icon className="w-3 h-3" />
          {statusCfg.label}
        </span>
      </div>

      <div className={`text-xs px-3 py-2 rounded-lg ${statusCfg.descCls}`}>{statusCfg.desc}</div>

      <div className="space-y-1.5">
        <div className="flex items-baseline justify-between text-xs">
          <span className="text-dim">Способ</span>
          <span className="font-semibold">{methodLabel}</span>
        </div>
        <div className="flex items-baseline justify-between text-xs gap-2">
          <span className="text-dim shrink-0">Получатель</span>
          <button
            type="button"
            onClick={copyDestination}
            className="font-mono text-[11px] text-foreground/80 truncate flex items-center gap-1 active:scale-95 transition-transform"
            title="Копировать"
          >
            <span className="truncate max-w-[180px]">{p.destination}</span>
            {copied ? <Check className="w-3 h-3 text-[#38e0a5] shrink-0" /> : <Copy className="w-3 h-3 text-muted-foreground shrink-0" />}
          </button>
        </div>
        {p.note && <div className="text-[11px] text-dim italic">«{p.note}»</div>}
      </div>

      {checkUrl && (
        <div className="space-y-1.5">
          {isCompleted && (
            <button
              type="button"
              onClick={openCheck}
              className="w-full bg-primary text-primary-foreground py-2.5 rounded-xl text-sm font-bold flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
            >
              <Ticket className="w-4 h-4" />
              Получить в CryptoBot
              <ExternalLink className="w-4 h-4" />
            </button>
          )}
          <button
            type="button"
            onClick={copyCheckUrl}
            className="w-full bg-white/[0.05] text-foreground py-2.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
          >
            {checkCopied ? <Check className="w-4 h-4 text-[#38e0a5]" /> : <Copy className="w-4 h-4 text-muted-foreground" />}
            Копировать ссылку чека
          </button>
        </div>
      )}

      {p.auto_error && !isCompleted && (
        <div className="text-[11px] text-[#f0b04e] flex items-start gap-1.5 bg-[rgba(240,176,78,0.06)] p-2 rounded-lg">
          <AlertCircle className="w-3 h-3 mt-0.5 shrink-0" />
          <span>{p.auto_error.slice(0, 150)}</span>
        </div>
      )}
    </div>
  );
}


/** Карточка вывода: сумма с датой, бейдж статуса, пояснение, способ и получатель. */
function PayoutsSkeleton() {
  return (
    <>
      {[0, 1, 2].map((i) => (
        <SkeletonCard key={i} className="rounded-[20px] p-4 space-y-3">
          <div className="flex items-start justify-between gap-2">
            <div className="space-y-1.5">
              <SkeletonLine size="title" className="w-24" />
              <SkeletonLine size="xs" className="w-28" />
            </div>
            <Skeleton className="h-[22px] w-24 rounded-full shrink-0" />
          </div>
          <Skeleton className="h-[30px] w-full rounded-lg" />
          <div className="space-y-1.5">
            {[0, 1].map((r) => (
              <div key={r} className="flex items-baseline justify-between gap-2">
                <SkeletonLine size="sm" className="w-20" />
                <SkeletonLine size="sm" className="w-28" />
              </div>
            ))}
          </div>
        </SkeletonCard>
      ))}
    </>
  );
}
